// packages/ui/src/date/format.ts
//
// Centralised date/time display + conversion helpers for the web app.
//
// Two distinct kinds of value flow through the UI:
//
//   * Date-only  (Postgres `date`: expected/actual close, compelling event).
//     These have NO time and NO timezone — they are calendar dates. We anchor
//     them to the company timezone (America/Los_Angeles, where the company is
//     headquartered) so every viewer sees the *same stored day* regardless of
//     their browser timezone. This fixes the "Close column shows the day
//     before" bug: `new Date('2026-06-15')` is UTC midnight, which renders as
//     the prior day for viewers west of UTC.
//
//   * Date-time  (Postgres `timestamptz`: activity due/start, created/updated).
//     These are real instants. We render them in the *viewer's* local timezone
//     by default, or an explicit timezone the user picks.
//
// No timezone library: native Intl gives us DST-correct offsets for any IANA
// zone, which is all we need.

/** The company headquarters timezone. Bare calendar dates are interpreted and
 *  displayed in this zone so they are stable across viewers. */
export const COMPANY_TIMEZONE = "America/Los_Angeles";

/** Locale used for display formatting. The app is US-centric (person records
 *  default to America/Los_Angeles); override per call site if ever needed. */
const DEFAULT_LOCALE = "en-US";

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Offset of `timeZone` at instant `at`, in minutes, positive east of UTC
 * (e.g. America/Los_Angeles in summer → -420). DST-aware because it asks Intl
 * what the wall clock is in that zone at that exact instant.
 */
export function tzOffsetMinutes(timeZone: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(at);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return Math.round((asUtc - at.getTime()) / 60000);
}

/**
 * The UTC instant for a wall-clock time interpreted in `timeZone`.
 * Refines once so DST boundary times settle on the correct offset.
 */
function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
  let offset = tzOffsetMinutes(timeZone, new Date(naiveUtc));
  let utc = naiveUtc - offset * 60000;
  offset = tzOffsetMinutes(timeZone, new Date(utc));
  utc = naiveUtc - offset * 60000;
  return new Date(utc);
}

/**
 * Format a date-only value (`YYYY-MM-DD`, a Date, or null) as a calendar date
 * anchored to the company timezone, e.g. "Jun 15, 2026". Returns "" for empty.
 *
 * Bare-date strings are shown verbatim for every viewer; full timestamps are
 * reduced to their calendar date *in the company timezone*.
 */
export function formatDateOnly(
  value: string | Date | null | undefined,
  opts?: { timeZone?: string; locale?: string },
): string {
  if (value == null || value === "") return "";
  const timeZone = opts?.timeZone ?? COMPANY_TIMEZONE;
  const locale = opts?.locale ?? DEFAULT_LOCALE;

  let instant: Date;
  if (typeof value === "string") {
    const m = DATE_ONLY_RE.exec(value.trim());
    // Anchor a bare date to noon in the company zone: noon is far from the
    // midnight DST/offset edge, so the calendar day never slips.
    instant = m
      ? zonedTimeToUtc(Number(m[1]), Number(m[2]), Number(m[3]), 12, 0, timeZone)
      : new Date(value);
  } else {
    instant = value;
  }
  if (Number.isNaN(instant.getTime())) return "";

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(instant);
}

/**
 * Format a real instant (`timestamptz` / ISO string / Date) as date + time.
 * Defaults to the viewer's local timezone; pass `timeZone` to pin it.
 * Returns "" for empty / invalid input.
 */
export function formatDateTime(
  value: string | Date | null | undefined,
  opts?: { timeZone?: string; locale?: string },
): string {
  if (value == null || value === "") return "";
  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return "";

  return new Intl.DateTimeFormat(opts?.locale ?? DEFAULT_LOCALE, {
    timeZone: opts?.timeZone, // undefined → viewer-local
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(instant);
}

/**
 * Render a UTC instant as a `YYYY-MM-DDTHH:mm` wall-clock string in `timeZone`,
 * suitable as the `value` of an `<input type="datetime-local">`.
 */
export function utcToZonedWallClock(value: string | Date, timeZone: string): string {
  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(instant);
  const m: Record<string, string> = {};
  for (const part of parts) if (part.type !== "literal") m[part.type] = part.value;
  return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
}

/**
 * Interpret a `YYYY-MM-DDTHH:mm` wall-clock string as a time in `timeZone` and
 * return the corresponding UTC instant. Returns null for empty / malformed
 * input. DST-correct (e.g. a spring-forward wall clock resolves to PDT).
 */
export function zonedWallClockToUtc(wallClock: string, timeZone: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec((wallClock ?? "").trim());
  if (!m) return null;
  return zonedTimeToUtc(
    Number(m[1]),
    Number(m[2]),
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    timeZone,
  );
}
